// Anbindung an die API.
//
// Jede schreibende Anfrage liefert den vollständigen Bestand zurück. Die
// Oberfläche ersetzt damit ihren Zustand am Stück, statt einzelne Felder
// nachzupflegen — so kann keine Kennzahl stehenbleiben, während die Liste
// darunter sich schon geändert hat.

import type { Benutzer, Benutzerkonto, Jahreszahlen, KategorieSumme, Portfolio } from "./typen.ts";

export class ApiFehler extends Error {
  status: number;
  constructor(status: number, meldung: string) {
    super(meldung);
    this.status = status;
  }
}

async function ruf<T>(pfad: string, optionen: RequestInit = {}): Promise<T> {
  // Content-Type nur setzen, wenn wirklich ein JSON-Körper mitgeht.
  //
  // Ein POST mit "Content-Type: application/json" und leerem Körper lehnt
  // Fastify mit 400 ab ("Body cannot be empty") — zu Recht, denn ein leerer
  // Körper ist kein gültiges JSON. Das hat alle Aufrufe ohne Nutzlast
  // getroffen: Abmelden, "auf allen Geräten abmelden" und den Knopf
  // "→ Mangel" an einer Inventarposition.
  const istJson = typeof optionen.body === "string";
  const antwort = await fetch(`/api${pfad}`, {
    // Cookies gehören dazu, sonst gilt jede Anfrage als nicht angemeldet.
    credentials: "same-origin",
    ...optionen,
    headers: istJson
      ? { "Content-Type": "application/json", ...optionen.headers }
      : optionen.headers,
  });

  if (antwort.status === 204) return undefined as T;

  let daten: unknown = null;
  try { daten = await antwort.json(); } catch { /* leere oder nicht-JSON-Antwort */ }

  if (!antwort.ok) {
    const meldung = (daten as { fehler?: string } | null)?.fehler
      ?? `Der Server hat mit ${antwort.status} geantwortet.`;
    throw new ApiFehler(antwort.status, meldung);
  }
  return daten as T;
}

const senden = (methode: string) => <T>(pfad: string, koerper?: unknown) =>
  ruf<T>(pfad, { method: methode, body: koerper === undefined ? undefined : JSON.stringify(koerper) });

const post = senden("POST");
const patch = senden("PATCH");
const entferne = senden("DELETE");

export const api = {
  // ------------------------------------------------------------ Anmeldung
  ich: () => ruf<{ benutzer: Benutzer }>("/ich"),
  anmelden: (email: string, passwort: string) =>
    post<{ benutzer: Benutzer }>("/anmeldung", { email, passwort }),
  abmelden: () => post<{ ok: boolean }>("/abmeldung"),
  ueberallAbmelden: () => post<{ beendet: number }>("/abmeldung/ueberall"),

  // -------------------------------------------------------------- Bestand
  portfolio: () => ruf<Portfolio>("/portfolio"),

  objektAnlegen: (daten: unknown) => post<Portfolio>("/objekte", daten),
  objektAendern: (id: string, daten: unknown) => patch<Portfolio>(`/objekte/${id}`, daten),
  objektLoeschen: (id: string) => entferne<Portfolio>(`/objekte/${id}`),

  einheitAnlegen: (objektId: string, daten: unknown) =>
    post<Portfolio>(`/objekte/${objektId}/einheiten`, daten),
  einheitAendern: (id: string, daten: unknown) => patch<Portfolio>(`/einheiten/${id}`, daten),
  einheitLoeschen: (id: string) => entferne<Portfolio>(`/einheiten/${id}`),

  vertragAnlegen: (einheitId: string, daten: unknown) =>
    post<Portfolio>(`/einheiten/${einheitId}/mietvertraege`, daten),
  vertragAendern: (id: string, daten: unknown) => patch<Portfolio>(`/mietvertraege/${id}`, daten),
  vertragBeenden: (id: string, ende?: string) =>
    post<Portfolio>(`/mietvertraege/${id}/beenden`, ende ? { ende } : {}),
  vertragLoeschen: (id: string) => entferne<Portfolio>(`/mietvertraege/${id}`),

  postenAnlegen: (objektId: string, daten: unknown) =>
    post<Portfolio>(`/objekte/${objektId}/sanierung`, daten),
  postenAendern: (id: string, daten: unknown) => patch<Portfolio>(`/sanierung/${id}`, daten),
  postenLoeschen: (id: string) => entferne<Portfolio>(`/sanierung/${id}`),

  mangelAnlegen: (objektId: string, daten: unknown) =>
    post<Portfolio>(`/objekte/${objektId}/maengel`, daten),
  mangelAendern: (id: string, daten: unknown) => patch<Portfolio>(`/maengel/${id}`, daten),
  mangelLoeschen: (id: string) => entferne<Portfolio>(`/maengel/${id}`),
  mangelAusInventar: (inventarId: string) => post<Portfolio>(`/inventar/${inventarId}/mangel`),

  inventarAnlegen: (einheitId: string, daten: unknown) =>
    post<Portfolio>(`/einheiten/${einheitId}/inventar`, daten),
  inventarAendern: (id: string, daten: unknown) => patch<Portfolio>(`/inventar/${id}`, daten),
  inventarLoeschen: (id: string) => entferne<Portfolio>(`/inventar/${id}`),

  vorlageAnlegen: (daten: unknown) => post<Portfolio>("/vorlage", daten),
  vorlageAendern: (id: string, daten: unknown) => patch<Portfolio>(`/vorlage/${id}`, daten),
  vorlageLoeschen: (id: string) => entferne<Portfolio>(`/vorlage/${id}`),
  vorlageAnwenden: (einheitId: string | null, raeume?: string[]) =>
    post<Portfolio>("/vorlage/anwenden", { einheitId, raeume }),
  vorlageAusEinheit: (einheitId: string, umfang: "notwendig" | "alle") =>
    post<Portfolio>("/vorlage/aus-einheit", { einheitId, umfang }),

  // -------------------------------------------------------------- Konten
  // Kein Rechte- und Rollensystem: Wer angemeldet ist, darf Konten verwalten.
  // Der Server verhindert lediglich, dass man sich selbst oder das letzte
  // Konto aussperrt.
  konten: () => ruf<{ benutzer: Benutzerkonto[]; eigeneId: string }>("/benutzer"),
  kontoAnlegen: (daten: { email: string; name: string; passwort?: string }) =>
    post<{ benutzer: Benutzer; erzeugtesPasswort: string | null; alle: Benutzerkonto[] }>("/benutzer", daten),
  kontoAendern: (id: string, daten: { name?: string; aktiv?: boolean }) =>
    patch<{ benutzer: Benutzerkonto[] }>(`/benutzer/${id}`, daten),
  kontoPasswort: (id: string, daten: { passwort?: string; sitzungenBeenden?: boolean }) =>
    post<{ erzeugtesPasswort: string | null }>(`/benutzer/${id}/passwort`, daten),
  kontoLoeschen: (id: string) => entferne<{ benutzer: Benutzerkonto[] }>(`/benutzer/${id}`),

  // ------------------------------------------------------------- Finanzen
  bankkontoAnlegen: (daten: unknown) => post<Portfolio>("/konten", daten),
  bankkontoAendern: (id: string, daten: unknown) => patch<Portfolio>(`/konten/${id}`, daten),
  bankkontoLoeschen: (id: string) => entferne<Portfolio>(`/konten/${id}`),

  kategorieAnlegen: (daten: unknown) => post<Portfolio>("/kategorien", daten),
  kategorieAendern: (id: string, daten: unknown) => patch<Portfolio>(`/kategorien/${id}`, daten),
  kategorieLoeschen: (id: string) => entferne<Portfolio>(`/kategorien/${id}`),

  buchungAnlegen: (daten: unknown) => post<Portfolio>("/buchungen", daten),
  buchungAendern: (id: string, daten: unknown) => patch<Portfolio>(`/buchungen/${id}`, daten),
  buchungLoeschen: (id: string) => entferne<Portfolio>(`/buchungen/${id}`),

  darlehenAnlegen: (daten: unknown) => post<Portfolio>("/darlehen", daten),
  darlehenAendern: (id: string, daten: unknown) => patch<Portfolio>(`/darlehen/${id}`, daten),
  darlehenLoeschen: (id: string) => entferne<Portfolio>(`/darlehen/${id}`),
  rateBuchen: (id: string, daten: { datum?: string; konto_id?: string | null }) =>
    post<Portfolio>(`/darlehen/${id}/rate`, daten),

  finanzjahr: (jahr: number) =>
    ruf<{ zahlen: Jahreszahlen; kategorien: KategorieSumme[] }>(`/finanzen/jahr/${jahr}`),

  // -------------------------------------------------------------- Sonstiges
  aufgabeAnlegen: (daten: unknown) => post<Portfolio>("/aufgaben", daten),
  aufgabeAendern: (id: string, daten: unknown) => patch<Portfolio>(`/aufgaben/${id}`, daten),
  aufgabeAbhaken: (id: string, erledigt: boolean) =>
    post<Portfolio>(`/aufgaben/${id}/erledigt`, { erledigt }),
  aufgabeLoeschen: (id: string) => entferne<Portfolio>(`/aufgaben/${id}`),

  dokumentHochladen: (datei: Blob, name: string, felder: Record<string, string>) => {
    const formular = new FormData();
    for (const [k, v] of Object.entries(felder)) if (v) formular.append(k, v);
    formular.append("datei", datei, name);
    return ruf<Portfolio>("/dokumente", { method: "POST", body: formular });
  },
  dokumentAendern: (id: string, daten: unknown) => patch<Portfolio>(`/dokumente/${id}`, daten),
  dokumentLoeschen: (id: string) => entferne<Portfolio>(`/dokumente/${id}`),

  nachrichtAnlegen: (daten: unknown) => post<Portfolio>("/nachrichten", daten),
  nachrichtAendern: (id: string, daten: unknown) => patch<Portfolio>(`/nachrichten/${id}`, daten),
  nachrichtLoeschen: (id: string) => entferne<Portfolio>(`/nachrichten/${id}`),

  // -------------------------------------------------------------- Dateien
  /** Adresse eines Anhangs. Liegt hinter dem Login — ohne Sitzung kommt 401. */
  dateiAdresse: (id: string) => `/api/dateien/${id}`,

  dateiHochladen: (datei: Blob, name: string, entitaet: string, entitaetId: string, rolle = "anhang") => {
    const formular = new FormData();
    formular.append("entitaet", entitaet);
    formular.append("entitaetId", entitaetId);
    formular.append("rolle", rolle);
    formular.append("datei", datei, name);
    return ruf<Portfolio & { datei: { id: string } }>("/dateien", { method: "POST", body: formular });
  },

  dateiLoesen: (id: string, entitaet: string, entitaetId: string) =>
    entferne<Portfolio>(
      `/dateien/${id}?entitaet=${encodeURIComponent(entitaet)}&entitaetId=${encodeURIComponent(entitaetId)}`),
};

/**
 * Verkleinert ein Bild vor dem Hochladen — genau wie die alte App.
 *
 * Ein iPhone-Foto hat gut 4 MB; auf 1100 px verkleinert bleiben rund 200 kB.
 * Serverseitig zu skalieren bräuchte eine native Bildbibliothek im Container,
 * ohne dass der Nutzer etwas davon hätte.
 */
export function verkleinereBild(datei: File, maxKante = 1100): Promise<Blob> {
  return new Promise((erfuelle, verwirf) => {
    // PDFs und alles Nicht-Bildliche gehen unverändert durch.
    if (!datei.type.startsWith("image/")) return erfuelle(datei);

    const leser = new FileReader();
    leser.onerror = () => verwirf(new Error("Die Datei konnte nicht gelesen werden."));
    leser.onload = () => {
      const bild = new Image();
      bild.onerror = () => {
        // HEIC kann der Browser oft nicht zeichnen — dann eben unverändert.
        erfuelle(datei);
      };
      bild.onload = () => {
        const faktor = Math.min(1, maxKante / Math.max(bild.width, bild.height));
        const breite = Math.round(bild.width * faktor);
        const hoehe = Math.round(bild.height * faktor);
        const flaeche = document.createElement("canvas");
        flaeche.width = breite;
        flaeche.height = hoehe;
        const stift = flaeche.getContext("2d");
        if (!stift) return erfuelle(datei);
        stift.drawImage(bild, 0, 0, breite, hoehe);
        flaeche.toBlob((ergebnis) => erfuelle(ergebnis ?? datei), "image/jpeg", 0.72);
      };
      bild.src = leser.result as string;
    };
    leser.readAsDataURL(datei);
  });
}

/**
 * Ob eine Datei angehängt werden darf. Der Server prüft am Inhalt, aber eine
 * Rückmeldung vor dem Hochladen erspart die vergebliche Wartezeit.
 */
export const ERLAUBTE_TYPEN = ["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"];
export const MAX_DATEI = 12 * 1024 * 1024;
